/**
 * Gomila recently viewed.
 *
 * - Remembers the last 8 unique product ids (newest first) in localStorage, only when the visitor's privacy
 *   settings allow storing preferences. Blocked storage degrades to "no history", never to an error.
 * - Renders the cards through the Section Rendering API on /search, so names, prices, images and links are
 *   live Shopify data and deleted / unavailable products drop out.
 */
(() => {
  if (window.customElements.get('gomila-recently-viewed')) return;

  const STORAGE_KEY = 'gomila:recently-viewed';
  const HISTORY_LIMIT = 8;
  const CONSENT_TIMEOUT = 4000;

  const debug = (...args) => {
    if (/[?&]grv_debug=1/.test(window.location.search)) console.log('[recently-viewed]', ...args);
  };

  const history = {
    read() {
      try {
        const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
        if (!Array.isArray(parsed)) return [];
        const ids = parsed.map(Number).filter((id) => Number.isSafeInteger(id) && id > 0);
        return [...new Set(ids)].slice(0, HISTORY_LIMIT);
      } catch (e) {
        return [];
      }
    },

    write(ids) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids.slice(0, HISTORY_LIMIT)));
      } catch (e) {
        // Storage blocked or full: keep browsing, just without history.
      }
    },

    clear() {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch (e) {}
    }
  };

  /**
   * Resolves to true (allowed), false (denied) or null (unknown). Shopify's Customer Privacy API reports the store's
   * consent rules: regions without a consent requirement report "allowed", regions with one report "denied" until the
   * visitor accepts. If the API does not exist at all the store has no consent mechanism, so storage is allowed.
   */
  const readConsent = () => {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (privacy && typeof privacy.preferencesProcessingAllowed === 'function') {
      return Promise.resolve(Boolean(privacy.preferencesProcessingAllowed()));
    }

    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      const timer = window.setTimeout(() => resolve(null), CONSENT_TIMEOUT);

      try {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
          window.clearTimeout(timer);
          const loaded = window.Shopify.customerPrivacy;
          if (error || !loaded || typeof loaded.preferencesProcessingAllowed !== 'function') {
            resolve(null);
          } else {
            resolve(Boolean(loaded.preferencesProcessingAllowed()));
          }
        });
      } catch (e) {
        window.clearTimeout(timer);
        resolve(null);
      }
    });
  };

  const whenIdle = (callback) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 2000 });
    } else {
      window.setTimeout(callback, 200);
    }
  };

  class GomilaRecentlyViewed extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-grv-track]');
      this.prevButton = this.querySelector('[data-grv-prev]');
      this.nextButton = this.querySelector('[data-grv-next]');
      this.currentId = Number(this.dataset.currentId) || null;
      this.max = Math.min(Math.max(Number(this.dataset.max) || HISTORY_LIMIT, 1), HISTORY_LIMIT);
      this.loaded = false;

      this.onConsentCollected = () => this.start();
      this.onScroll = () => this.updateArrows();
      this.onPrev = () => this.scrollByPage(-1);
      this.onNext = () => this.scrollByPage(1);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.prevButton.addEventListener('click', this.onPrev);
      this.nextButton.addEventListener('click', this.onNext);
      window.addEventListener('resize', this.onScroll, { passive: true });
      document.addEventListener('visitorConsentCollected', this.onConsentCollected);

      this.start();
    }

    disconnectedCallback() {
      this.track.removeEventListener('scroll', this.onScroll);
      this.prevButton.removeEventListener('click', this.onPrev);
      this.nextButton.removeEventListener('click', this.onNext);
      window.removeEventListener('resize', this.onScroll);
      document.removeEventListener('visitorConsentCollected', this.onConsentCollected);
      if (this.observer) this.observer.disconnect();
      if (this.abortController) this.abortController.abort();
    }

    async start() {
      const consent = await readConsent();
      debug('consent', consent);

      if (consent === false) {
        history.clear();
        this.hideSection();
        return;
      }

      // Only an explicit "denied" blocks storage. If the consent API is unreachable the store has no consent banner.
      const stored = history.read();
      const visible = stored.filter((id) => id !== this.currentId).slice(0, this.max);

      if (this.currentId) {
        const next = [this.currentId, ...stored.filter((id) => id !== this.currentId)];
        whenIdle(() => history.write(next));
      }

      debug('current', this.currentId, 'stored', stored, 'visible', visible);

      if (visible.length === 0) {
        this.hideSection();
        return;
      }

      if (this.loaded) return;
      this.showSkeleton(visible.length);
      this.hidden = false;
      this.updateEditorNote(false);
      this.loadWhenNearViewport(visible);
    }

    hideSection() {
      this.track.replaceChildren();
      this.hidden = true;
      this.updateEditorNote(true);
    }

    updateEditorNote(show) {
      const note = this.querySelector('[data-grv-editor-note]');
      if (!note || !window.Shopify || !window.Shopify.designMode) return;
      note.hidden = !show;
      if (show) this.hidden = false;
    }

    showSkeleton(count) {
      const fragment = document.createDocumentFragment();
      for (let i = 0; i < count; i += 1) {
        const item = document.createElement('li');
        item.className = 'grv-card grv-card--skeleton';
        item.setAttribute('aria-hidden', 'true');
        item.innerHTML =
          '<span class="grv-card__link"><span class="grv-card__figure"></span>' +
          '<span class="grv-card__info"><span class="grv-card__name"></span><span class="grv-card__price"></span></span></span>';
        fragment.appendChild(item);
      }
      this.track.replaceChildren(fragment);
    }

    loadWhenNearViewport(ids) {
      if (this.observer) this.observer.disconnect();

      if (!('IntersectionObserver' in window)) {
        this.load(ids);
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.observer.disconnect();
            this.load(ids);
          }
        },
        { rootMargin: '600px 0px' }
      );
      this.observer.observe(this);
    }

    async load(ids) {
      if (this.loaded) return;
      this.loaded = true;

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const query = encodeURIComponent(ids.map((id) => `id:${id}`).join(' OR '));
      const url = `${root}search?type=product&q=${query}&section_id=${encodeURIComponent(this.dataset.sectionId)}`;

      this.abortController = new AbortController();

      try {
        const response = await fetch(url, { signal: this.abortController.signal, priority: 'low' });
        if (!response.ok) throw new Error(`Recently viewed request failed (${response.status})`);

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const cards = new Map();
        doc.querySelectorAll('[data-grv-results] .grv-card').forEach((card) => {
          cards.set(Number(card.dataset.productId), card);
        });

        // The search API ignores our ordering, so rebuild it from the history (newest first).
        debug('search returned', [...cards.keys()]);
        const ordered = ids.map((id) => cards.get(id)).filter(Boolean);

        if (ordered.length === 0) {
          this.hideSection();
          return;
        }

        this.track.replaceChildren(...ordered.map((card) => document.importNode(card, true)));
        this.track.scrollLeft = 0;
        this.updateArrows();
      } catch (error) {
        if (error.name === 'AbortError') return;
        debug('fetch failed', error);
        this.loaded = false;
        this.hideSection();
      }
    }

    updateArrows() {
      if (!this.track) return;
      const { scrollLeft, clientWidth, scrollWidth } = this.track;
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const offset = Math.abs(scrollLeft);
      const atStart = offset <= 4;
      const atEnd = offset + clientWidth >= scrollWidth - 4;

      this.prevButton.hidden = rtl ? atEnd : atStart;
      this.nextButton.hidden = rtl ? atStart : atEnd;
    }

    scrollByPage(direction) {
      const card = this.track.querySelector('.grv-card');
      if (!card) return;

      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const stride = card.getBoundingClientRect().width + gap;
      const perPage = Math.max(1, Math.floor((this.track.clientWidth + gap) / stride));
      const rtl = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.track.scrollBy({ left: direction * rtl * perPage * stride, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  }

  window.customElements.define('gomila-recently-viewed', GomilaRecentlyViewed);
})();
